<template>
  <v-card flat class="container--fluid">

    <v-card flat class="container--fluid bg-gray no-margins elevation-1 not-rounded">
      <v-card flat class="v-card__container">
        <v-card-title class="no-margins d-flex align-start">
          <h4 class="mb-2">Personer</h4>
          <v-spacer></v-spacer>
          <SimpleSearch v-show="simple" v-model="search" @search="doSearch" @switchMode="switchMode"/>
        </v-card-title>
        <AdvancedSearch v-show="!simple" v-model="advSearch" :searchFields="advancedSearchFields" @search="doSearch" @switchMode="switchMode" />
      </v-card>

      <v-card-text class="mt-8">
        <div class="overline">Visningsalternativ</div>
        <v-switch
          class="show-inactive"
          v-model="includeInactive"
          label="Visa inaktiva och spärrade konton (markeras med infoikon, genomstruken och halvgenomskinlig text)"
          @change="doSearch"
        ></v-switch>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="people"
      :loading="isLoading"
      :options.sync="options"
      :server-items-length="totalRows"
      :items-per-page="itemsPerPage"
      :search="search"
      item-key="id"
      :class="'ma-6'"
      :item-class= "row_classes"     
      @click:row="clickPerson"
    >
      <!-- Person lastname header -->
      <template slot="item.lastName" slot-scope="props">
        <div :class="[props.item.isActive ? '' : 'isInActiveBlack']">
          <template v-if="props.item.isProtected && currentUserRole === 'region'">
            {{ props.item.firstName }} {{ props.item.lastName }}
          </template>
          <template v-else>
            <router-link :to="'/people/' + props.item.id">
              {{ props.item.firstName }} {{ props.item.lastName }}
            </router-link>
          </template>
        </div>
      </template>
      <!-- Person personalNumber header -->
      <template slot="item.personalNumber" slot-scope="props">
        <div :class="[props.item.isActive ? '' : 'isInActiveBlack']" style="width: 150px">
          <CopyToClipboard :text="props.item.personalNumber">
            {{ props.item.personalNumber }}
          </CopyToClipboard>
        </div>
      </template>
      <!-- Person mobilephone header -->
      <template slot="item.mobilephone" slot-scope="props">
        <div :class="[props.item.isActive ? '' : 'isInActiveBlack']" style="width: 130px">
          {{ props.item.mobilephone }}
        </div>
      </template>
      <template slot="item.email" slot-scope="props">
        <div :class="[props.item.isActive ? '' : 'isInActiveBlack']">
          <a v-if="props.item.isActive && !(props.item.isProtected && currentUserRole === 'region')" class="emailcolumn" @click.stop :href="`mailto:${props.item.email}`">{{
            props.item.email
          }}</a>
          <template v-else>{{props.item.email}}</template>
        </div>
      </template>
      <template slot="item.address1" slot-scope="props">
        <div :class="[props.item.isActive ? '' : 'isInActiveBlack']" style="width: 230px">
          {{ props.item.address1 }}
        </div>
      </template>

      <!-- Person icons header -->
      <template slot="item.icons" slot-scope="props">
        <template >
          <div class="d-flex" v-if="props.item.isActive">

            <v-tooltip left v-if="props.item.isProtected">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon class="ml-1">$sl-user-protect</v-icon>
                </span>
              </template>
              <span>Skyddad
              </span>
            </v-tooltip>

            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <span v-on="on" v-if="props.item.apprenticeships && props.item.apprenticeships.length > 0">
                  <v-icon class="">$sl-apprentice</v-icon>
                </span>
              </template>
              <span v-if="props.item.apprenticeships && props.item.apprenticeships.length">
                {{
                  props.item.apprenticeships
                    .map((c) => getProfessionById(c.professionId).name)
                    .join(", ")
                }}
              </span>
            </v-tooltip>

            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon class="ml-1" v-if="hasTradeCert(props.item)">$sl-cert</v-icon>
                </span>
              </template>
              <span
                v-if="
                  Boolean(
                    props.item.tradeCertificates && props.item.tradeCertificates.length
                  )
                "
              >
                {{ props.item.tradeCertificates.map((c) => c.profession.name).join(", ") }}
              </span>
            </v-tooltip>

            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon v-if="hasExemtion(props.item)" class="ml-1">
                    $sl-user-check</v-icon
                  >
                </span>
              </template>
              <span
                v-if="
                  Boolean(
                    props.item.instructorExemptions &&
                      props.item.instructorExemptions.length
                  )
                "
              >
                {{
                  props.item.instructorExemptions.map((c) => c.profession.name).join(", ")
                }}
              </span>
            </v-tooltip>
          </div>
          <div v-else>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon class="ml-1">$sl-user-block</v-icon>
                </span>
              </template>
              <span>Inaktiv          
              </span>
            </v-tooltip>          
          </div>
        </template>
      </template>
    </v-data-table>

    <v-card-text>
      <p class="overline small">Förklaring</p>
      <div class="d-flex caption mt-n2">
        <v-icon class="mr-2">$sl-apprentice</v-icon>
        <span class="mt-1">Personen är en lärling</span>
      </div>
      <div class="d-flex caption mt-2">
        <v-icon class="mr-2">$sl-cert</v-icon>
        <span class="mt-1">Personen har ett eller flera yrkesbevis</span>
      </div>
      <div class="d-flex mt-2 caption">
        <v-icon class="mr-2">$sl-user-check</v-icon>
        <span class="mt-1">Personen har handledardispens</span>
      </div>
      <div class="d-flex mt-2 caption">
        <v-icon class="mr-2">$sl-user-protect</v-icon>
        <span class="mt-1">Personen har skyddad information</span>
      </div>
    </v-card-text>
    <v-btn data-cy="add-new-person" absolute bottom right fab @click="showAddPersonModal()" :disabled="!canCreatePerson">
      <v-icon class="ml-1">$sl-add</v-icon>
    </v-btn>

    <v-dialog v-model="newModal" max-width="600px">
      <PersonNewModal
        @close="newModal = false"
        @manual="addPersonManual"
        @person="addPersonSPAR"
      />
    </v-dialog>

  </v-card>
</template>

<script>
import API from "@/services/api.js";
import {Criterias, CanIDo} from "@/components/mixins";
import { mapGetters, mapActions } from "vuex";
import { pageSize } from "@/store/constants";
import PersonNewModal from "@/components/person/PersonNewModal";
import SimpleSearch from "@/components/search/Simple";
import AdvancedSearch from "@/components/search/Advanced";
import CopyToClipboard from '@/components/common/CopyToClipboard'

// @group Person
// PersonList component
export default {
  name: "PersonList",

  components: {
    PersonNewModal,
    SimpleSearch,
    AdvancedSearch,
    CopyToClipboard,
  },

  mixins: [Criterias, CanIDo],

  data: () => ({
    includeInactive: false,
    simple: true,
    search: "",
    tab: null,
    showCreate: false,
    showList: true,
    options: {},
    itemsPerPage: pageSize,
    totalRows: 0,
    people: [],
    advSearch: {},
    //- For fragment criterias
    criteria: {},
    doneInitialSearch: false,
    newModal: false,
  }),

  created() {
    console.log("Person lists - created");

    //- Get criteria from url hash fragment
    const locationCriteria = this.getCriteriaFromLocation("c");

    //- Set values if we have a criteria
    if (locationCriteria) {
      this.criteria = locationCriteria;
      this.simple = !this.criteria.advancedMode;
      this.search = this.criteria.search;
      this.advSearch = this.criteria.advSearch;
      this.includeInactive = this.criteria.includeInactive
      this.options = {
        page: this.criteria.page,
        itemsPerPage: this.criteria.pageSize,
        sortBy: [this.criteria.sortBy],
        sortDesc: [this.criteria.sortDesc],
      };
    }
  },

  computed: {
    ...mapGetters("app", ["isLoading"]),
    ...mapGetters("basic", ['basicProfessions', "getProfessionById"]),
    ...mapGetters('currentuser', ['currentUserRole']),

    headers() {
      return [
        { text: "Namn", value: "lastName" },
        { text: "Personnummer", value: "personalNumber" },
        { text: "Mobiltelefon", value: "mobilephone" },
        { text: "E-post", value: "email", width: "250" },
        { text: "Adress", value: "address1" },
        { text: "Stat.", value: "icons", width: "90" },
      ];
    },

    professions() {
      return this.basicProfessions.map(p => {return {value: p.id, text: p.name}})
    },

    advancedSearchFields() {
      return {
        personSearch: {
          heading: 'Fritext',
          chipLabel: 'Fritext',
          label: "Sök fritt efter personer vars information innehåller:"
        },
        placeholder1: {
          heading: '',
          chipLabel: '',
          label: '',
          placeholder: true
        },
        personFirstName: {
          heading: 'Förnamn',
          chipLabel: 'Förnamn',
          label: "Sök efter personer vars förnamn innehåller:"
        },
        personLastName: {
          heading: 'Efternamn',
          chipLabel: 'Efternamn',
          label: "Sök efter personer vars efternamn innehåller:",
        },
        personPersonalNumber: {
          heading: 'Personnummer',
          chipLabel: 'Pnr',
          label: "Sök efter personer vars personnummer innehåller:"
        },
        personStatus: {
          heading: 'Status',
          chipLabel: 'Status',
          label: "Sök efter personer vars status är:",
          items:[
              { value: 'all', text: 'Alla' },
              { value: 'active', text: 'Aktiv' },
              { value: 'inactive', text: 'Inaktiv' },
              { value: 'locked', text: 'Spärrad' },
            ],
          type: "select",
        },
        personMobile: {
          heading: 'Mobiltelefonnummer',
          chipLabel: 'Mobil',
          label: "Sök efter personer vars mobiltelefonnummer innehåller:"
        },
        personEmail: {
          heading: 'E-post',
          chipLabel: 'E-post',
          label: "Sök efter personer vars e-postadress innehåller:"
        },
        personProfession: {
          heading: 'Yrke',
          chipLabel: 'Yrke',
          label: "Sök på yrke som är:",
          items: this.professions,
          type: "select",
        },
        personType: {
          heading: 'Typ',
          chipLabel: 'Typ',
          label: "Sök på typ som är:",
          items: [
              { value: 'allApprentices', text: 'Lärlingar - alla' },
              { value: 'apprentice', text: 'Lärling i GU/K-tid' },
              { value: 'apprenticeInBasicEducation', text: 'Lärling i grundutbildning' },
              { value: 'apprenticeInQualifyingTime', text: 'Lärling i kvalificeringstid' },
              { value: 'employee', text: 'Yrkesperson' },
              { value: 'employeeWithTradeCertificate', text: 'Yrkesperson med yrkesbevis' },
              { value: 'personWithExemption', text: 'Person med handledardispens' },
              { value: 'personAsEmployee', text: 'Personal på företag' },
            ],
          type: "select",
        },
        personCompanyName: {
          heading: 'Företags / Arbetsgivares namn',
          chipLabel: 'Ftgnamn',
          label: "Sök efter personer vars arbetsgivares namn innehåller:"
        },
        personCompanyOrgNo: {
          heading: 'Företags / Arbetsgivares organisationsnummer',
          chipLabel: 'Orgno',
          label: "Sök efter personer vars arbetsgivares organisationsnummer innehåller:"
        },
      }
    }
  },

  asyncComputed: {
    canCreatePerson(){
      return this.canIDo('person', 'create');
    },
  },

  watch: {
    options: {
      handler() {
        this.fetchPeople();
      },
      deep: true,
    },
  },

  async beforeRouteLeave (to, from, next) {
    if (to && to.name === "PersonDetail") {
      await this.setSelectedPerson({})
    }
    next()
  },

  methods: {
    ...mapActions("person", ["setSelectedPerson"]),

    row_classes(item) {
        if (item.isActive) {
          return "clickable"; //can also return multiple classes e.g ["orange","disabled"]
        } 
    },

    switchMode() {
      this.simple=!this.simple
      if (this.simple) {
        this.advSearch = {}
      } else {
        this.search = ""
      }
      this.doSearch()
    },

    doSearch() {
      if (this.options.page > 1) {
        this.options.page = 1;
      } else {
        this.fetchPeople();
      }
    },
    getTradeCertText(item) {
      let ret = "";
      ret = item.employed.tradeCertificates.map((tc) => tc.profession.name).join(",");
      return ret;
    },
    hasTradeCert(item) {
      if (item.tradeCertificates) {
        const findApproved = item.tradeCertificates.find((x) => x.approvedById > 0);
        if (findApproved) {
          return true;
        }
      }
      return false;
    },
    hasExemtion(item) {
      if (item && item.instructorExemptions) {
        const findApproved = item.instructorExemptions.find((x) => x.approvedById > 0);
        if (findApproved) {
          return true;
        }
      }
      return false;
    },
    // @vuese
    // Used to fetch person list
    // @arg The argument is object representing search criteries
    fetchPeople() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const startAt = (page - 1) * this.itemsPerPage;

      let search=this.search
      if(this.search && this.search.length>3 && (this.search.substring(0,2)=='20' || this.search.substring(0,2)=='19'))
      {
        search = this.search.replace('-', '')  
      }

      this.criteria = {
        page: page,
        startAt: startAt ? startAt : 0,
        sortBy: sortBy ? sortBy[0] : "lastName",
        sortDesc: sortDesc ? sortDesc[0] : "ASC",
        advancedMode: !this.simple,
        search: search,
        pageSize: this.itemsPerPage,
        includeInactive: this.includeInactive,
        advSearch: this.advSearch,
      };
      // @vuese
      //--Get the person list
      API.listPersons(this.criteria)
        .then((response) => {
          // @vuese
          //- Set fragment criteria
          this.setCriteriaToLocation("c", this.criteria);
          this.people = response.data.rows;
          this.totalRows = response.data.count;
        })
        .catch((error) => {
          console.log("There was an error:", error.response);
        });
    },
    
    async clickPerson(item) {
      if (item.isProtected && this.currentUserRole === 'region') {
        this.$dialog.error({
          text: 'Denna person har skyddad data som bara kan visas av BYNs kansli och administratörer.',
          title: 'Skyddad person',
          icon: false,
          actions: {false: 'Stäng'}
        })
        return
      }
      if (item.isActive==true){
        await this.setSelectedPerson({})
        this.$nextTick(() =>
          this.$router.push({ name: "PersonDetail", params: { id: item.id } })
        );
      }    
    },

    async showAddPersonModal() {
      await this.setSelectedPerson({})
      this.newModal = true;
    },

    async addPersonManual() {
      this.newModal = false;
      await this.setSelectedPerson({})
      this.$nextTick(() => {
        this.$router.push({ name: "PersonNewDetail" });
      });
    },
    /**
     * @vuese
     * Used to set initialise values to an null fields
     * @arg The param is tan object representing person
     */
    addPersonSPAR(person) {
      this.newModal = false;
      this.setSelectedPerson(person);
      this.$nextTick(() => {
        this.$router.push({ name: "PersonNewDetail" });
      });
    },
  },
};
</script>

<style>
  .show-inactive .v-input__slot > label.v-label.theme--light {
    font-size: 14px !important;
  }
</style>
